/* photo strip - drag / auto-scroll

   Scrolls a real overflow-x container rather than transforming a track, so the
   native scrollbar, touch momentum and shift-wheel all work. A wrap is a jump
   in scrollLeft, so every path here sets scrollLeft directly rather than using
   scrollTo({behavior:'smooth'}). Auto-scroll accumulates sub-pixel drift in a
   float and only commits whole pixels: scrollLeft is integer-quantised, so
   adding 0.4 per frame would round to zero and never move. */

(function () {
  'use strict';

  var ROOT = '[data-yg-photo-strip]';
  var INTENT_THRESHOLD = 10;
  var DRAG_RELEASE_PAUSE_MS = 1600;
  var VELOCITY_DECAY = 0.94;
  var VELOCITY_FLOOR = 0.05;
  var WHEEL_INTENT = 1;
  /* section setting 1-20 maps onto this many px/frame */
  var SPEED_UNIT = 0.14;

  var instances = Object.create(null);
  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');

  function stripEditorAttrs(root) {
    var els = root.querySelectorAll('[data-shopify-editor-block-id], [data-block-id]');
    for (var i = 0; i < els.length; i++) {
      var attrs = Array.prototype.slice.call(els[i].attributes);
      for (var j = 0; j < attrs.length; j++) {
        var name = attrs[j].name;
        if (name.indexOf('data-shopify') === 0 || name === 'data-block-id') {
          els[i].removeAttribute(name);
        }
      }
    }
  }

  /* Duplicates the server-rendered tiles so scrollLeft can wrap without a
     visible end. Wrapping needs a full viewport of runway past the wrap point,
     so a set narrower than the viewport needs several copies. Returns the width
     of one original set, which is the wrap period. */
  function ensureClones(track, viewportWidth) {
    var originals = Array.prototype.slice.call(track.children);
    if (!originals.length) return 0;

    if (track.dataset.psCloned === 'true') {
      return parseFloat(track.dataset.psSegment) || 0;
    }

    var setWidth = track.scrollWidth;
    /* +1 so there is always at least one whole spare set beyond the runway */
    var copies = setWidth > 0 ? Math.ceil(viewportWidth / setWidth) + 1 : 1;
    var originalCount = originals.length;

    for (var c = 0; c < copies; c++) {
      for (var i = 0; i < originals.length; i++) {
        var clone = originals[i].cloneNode(true);
        stripEditorAttrs(clone);
        clone.dataset.psClone = 'true';
        clone.setAttribute('aria-hidden', 'true');
        var focusables = clone.querySelectorAll('[tabindex="0"], a, button');
        for (var k = 0; k < focusables.length; k++) {
          focusables[k].setAttribute('tabindex', '-1');
        }
        track.appendChild(clone);
      }
    }

    track.dataset.psCloned = 'true';
    track.dataset.psSets = String(copies + 1);
    track.dataset.psOriginals = String(originalCount);
    /* Measure the period AFTER cloning. Tiles overlap via a negative margin on
       `.yg-ps__tile + .yg-ps__tile`, which does not exist before the first
       clone is appended - so the pre-clone scrollWidth is one margin wider than
       the true repeat distance and the strip would drift sideways each lap. */
    track.dataset.psSegment = String(measureSegment(track));
    return parseFloat(track.dataset.psSegment);
  }

  /**
   * True repeat distance: the left offset between the first original tile
   * and its first clone. Falls back to an even division of scrollWidth if
   * the children are not where we expect.
   *
   * @param {Element} track
   * @returns {number}
   */
  function measureSegment(track) {
    var originalCount = parseFloat(track.dataset.psOriginals) || 0;
    var first = track.children[0];
    var firstClone = track.children[originalCount];
    if (first && firstClone) {
      var period = firstClone.offsetLeft - first.offsetLeft;
      if (period > 0) return period;
    }
    var sets = parseFloat(track.dataset.psSets) || 2;
    return track.scrollWidth / sets;
  }

  function attach(root) {
    var viewport = root.querySelector('[data-yg-ps-viewport]');
    var track = root.querySelector('[data-yg-ps-track]');
    if (!viewport || !track || !track.children.length) return null;

    var destroyed = false;
    var rafId = null;

    var speed = (parseFloat(root.dataset.autoscroll) || 0) * SPEED_UNIT;
    var segment = ensureClones(track, Math.max(viewport.clientWidth, window.innerWidth));
    var drift = 0;
    var dragging = false;
    var startX = 0;
    var startY = 0;
    var startScroll = 0;
    var velocity = 0;
    var lastX = 0;
    var lastT = 0;
    var idleTimer = null;
    var paused = false;
    var intent = 'pending';

    function measure() {
      if (!segment) segment = parseFloat(track.dataset.psSegment) || 0;
    }

    /* Both wraps land strictly INSIDE the band: landing exactly on 0 or on
       segment would re-trigger the opposite wrap on the next frame and
       ping-pong. scrollLeft can never go negative - the browser clamps it. */
    function wrap() {
      if (segment <= 0) return;
      /* Clone count was sized against the viewport at init; if the window is
         later widened past that there is no longer a whole set of runway, so
         fall back to a plain finite scroller until the next load re-clones. */
      if (viewport.scrollWidth - viewport.clientWidth < segment) return;
      var x = viewport.scrollLeft;
      if (x >= segment) {
        viewport.scrollLeft = x - segment + 1;
      } else if (x <= 0) {
        viewport.scrollLeft = segment - 1;
      }
    }

    function frame() {
      if (destroyed) return;

      if (!dragging) {
        if (Math.abs(velocity) > VELOCITY_FLOOR) {
          drift += velocity;
          velocity *= VELOCITY_DECAY;
        } else if (!paused && speed > 0 && !REDUCED.matches) {
          drift += speed;
        }

        if (Math.abs(drift) >= 1) {
          var whole = drift > 0 ? Math.floor(drift) : Math.ceil(drift);
          viewport.scrollLeft += whole;
          drift -= whole;
          wrap();
        }
      }
      rafId = requestAnimationFrame(frame);
    }

    function pauseFor(ms) {
      paused = true;
      clearTimeout(idleTimer);
      idleTimer = setTimeout(function () {
        paused = false;
      }, ms);
    }

    function onPointerDown(event) {
      /* A native scrollbar lives inside the viewport's border box but below its
         clientHeight. offsetY is relative to event.target - whichever tile is
         under the cursor - so the test must use the viewport's own box. */
      if (event.clientY - viewport.getBoundingClientRect().top > viewport.clientHeight) return;
      if (event.target.closest && event.target.closest('a, button')) return;

      dragging = true;
      intent = 'pending';
      velocity = 0;
      drift = 0;
      startX = event.clientX;
      startY = event.clientY;
      startScroll = viewport.scrollLeft;
      lastX = event.clientX;
      lastT = performance.now();
      viewport.classList.add('is-dragging');
      clearTimeout(idleTimer);
      paused = true;
    }

    function resolveIntent(dx, dy, event) {
      if (intent !== 'pending') return intent === 'horizontal';
      if (Math.abs(dx) < INTENT_THRESHOLD && Math.abs(dy) < INTENT_THRESHOLD) return false;

      intent = Math.abs(dx) > Math.abs(dy) ? 'horizontal' : 'vertical';
      if (intent === 'horizontal') {
        viewport.setPointerCapture(event.pointerId);
        return true;
      }

      dragging = false;
      viewport.classList.remove('is-dragging');
      clearTimeout(idleTimer);
      paused = false;
      return false;
    }

    function onPointerMove(event) {
      if (!dragging) return;
      var dx = event.clientX - startX;
      var dy = event.clientY - startY;
      if (!resolveIntent(dx, dy, event)) return;

      event.preventDefault();
      viewport.scrollLeft = startScroll - dx;
      wrap();

      var now = performance.now();
      var dt = now - lastT || 16;
      velocity = ((lastX - event.clientX) / dt) * 16;
      lastX = event.clientX;
      lastT = now;
    }

    function endDrag(event) {
      if (!dragging) return;
      dragging = false;
      viewport.classList.remove('is-dragging');
      if (event && event.pointerId != null && viewport.hasPointerCapture(event.pointerId)) {
        viewport.releasePointerCapture(event.pointerId);
      }
      pauseFor(DRAG_RELEASE_PAUSE_MS);
    }

    /* native wheel and scrollbar drags move scrollLeft without any handler
       above firing, so re-check the wrap from the scroll event too */
    function onScroll() {
      if (dragging) return;
      wrap();
    }

    /* only a horizontal gesture scrubs the strip; shift+wheel is horizontal
       too, but some browsers report it on deltaY */
    function onWheel(event) {
      if (dragging) return;
      var horizontal = event.shiftKey
        ? Math.max(Math.abs(event.deltaX), Math.abs(event.deltaY))
        : Math.abs(event.deltaX);
      var vertical = event.shiftKey ? 0 : Math.abs(event.deltaY);
      if (horizontal < WHEEL_INTENT || horizontal <= vertical) return;
      pauseFor(DRAG_RELEASE_PAUSE_MS);
    }

    function onResize() {
      measure();
      wrap();
    }

    viewport.addEventListener('pointerdown', onPointerDown);
    viewport.addEventListener('pointermove', onPointerMove);
    viewport.addEventListener('pointerup', endDrag);
    viewport.addEventListener('pointercancel', endDrag);
    viewport.addEventListener('scroll', onScroll, { passive: true });
    viewport.addEventListener('wheel', onWheel, { passive: true });
    window.addEventListener('resize', onResize);

    measure();
    /* start one whole set in, so the first backward drag has runway */
    viewport.scrollLeft = segment > 0 ? segment - 1 : 0;
    /* images are lazy and mostly unsized until they decode, so the segment
       measured at init can be short - re-measure once layout settles */
    var recalcTimer = setTimeout(function () {
      if (destroyed) return;
      var settled = measureSegment(track);
      if (settled > 0 && Math.abs(settled - segment) > 2) {
        segment = settled;
        track.dataset.psSegment = String(segment);
        viewport.scrollLeft = segment - 1;
      }
      wrap();
    }, 400);
    rafId = requestAnimationFrame(frame);

    return {
      destroy: function () {
        destroyed = true;
        if (rafId) cancelAnimationFrame(rafId);
        clearTimeout(idleTimer);
        clearTimeout(recalcTimer);
        viewport.removeEventListener('pointerdown', onPointerDown);
        viewport.removeEventListener('pointermove', onPointerMove);
        viewport.removeEventListener('pointerup', endDrag);
        viewport.removeEventListener('pointercancel', endDrag);
        viewport.removeEventListener('scroll', onScroll);
        viewport.removeEventListener('wheel', onWheel);
        window.removeEventListener('resize', onResize);
      },
    };
  }

  function init() {
    var roots = document.querySelectorAll(ROOT);
    for (var i = 0; i < roots.length; i++) {
      var root = roots[i];
      var sectionId = root.dataset.sectionId;
      if (!sectionId || instances[sectionId]) continue;

      var controller = attach(root);
      if (controller) instances[sectionId] = controller;
    }
  }

  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:unload', function (event) {
    var sectionId = event.detail && event.detail.sectionId;
    if (!sectionId || !instances[sectionId]) return;
    instances[sectionId].destroy();
    delete instances[sectionId];
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
